<template>
  <div class="page-wrap">
    <div v-if="loaded">

      <!-- ── Page Header ── -->
      <div class="page-header">
        <div class="page-header__left">
          <button class="btn-back" @click="$router.back()">
            <i class="fa fa-arrow-left"></i>
          </button>
          <div>
            <h1 class="page-title">Invoice DD00{{ invoice.code }}</h1>
            <div class="page-meta">
              <span class="meta-item"><i class="fa fa-calendar"></i> {{ formatDate(invoice.created_at) }}</span>
              <span class="meta-item"><i class="fa fa-home"></i> {{ invoice.provider }}</span>
              <span class="meta-item">
                <i :class="invoice.buyer_type === 'admin' ? 'fa fa-shield' : 'fa fa-user'"></i>
                {{ invoice.buyer_type }}
              </span>
              <span class="meta-item" v-if="invoice.cart?.meta.reference">
                <i class="fa fa-hashtag"></i>
                {{ invoice.cart?.meta.reference.tap_id || invoice.cart?.meta.reference.track }}
              </span>
            </div>
          </div>
        </div>
      </div>

      <!-- ── Info Grid ── -->
      <div class="info-grid">
        <div class="info-card">
          <div class="info-card__label">Invoice Code</div>
          <div class="info-card__value mono">DD00{{ invoice.code }}</div>
        </div>
        <div class="info-card info-card--link" @click="$router.push(`/carts/${invoice.cart_id}`)">
          <div class="info-card__label">Cart ID</div>
          <div class="info-card__value link-cell">#{{ invoice.cart_id }}</div>
        </div>
        <div
            class="info-card"
            :class="{ 'info-card--link': invoice.cart?.user.id }"
            @click="invoice.cart?.user.id ? $router.push(`/users/${invoice.cart?.user.id}`) : ''"
        >
          <div class="info-card__label">Customer Name</div>
          <div class="info-card__value" :class="{ 'link-cell': invoice.cart?.user.id }">
            {{ invoice.customer_name }}
          </div>
        </div>
        <div class="info-card">
          <div class="info-card__label">Payable</div>
          <div class="info-card__value payable-val">
            {{ invoice.payable.amount }}
            <span class="currency">{{ invoice.payable.currency.code }}</span>
          </div>
        </div>
      </div>

      <!-- ── Items Table ── -->
      <div class="table-panel">
        <div class="table-panel__header">
          <div class="panel-title"><i class="fa fa-list"></i> Invoice Items</div>
          <button class="btn-edit-all" @click="editDiscounts()">
            <i class="fa fa-edit"></i> Edit All Discounts
          </button>
        </div>
        <div class="table-responsive-wrap">
          <table class="dd-table">
            <thead>
            <tr>
              <th>ID</th>
              <th>OP Invoice</th>
              <th>Title</th>
              <th>Additional Services</th>
              <th>Gift</th>
              <th>Order</th>
              <th>State</th>
              <th>Status</th>
              <th>Purchase</th>
              <th>Selling</th>
              <th>VAT</th>
              <th>Credit</th>
              <th>Payment</th>
              <th>Supplier</th>
              <th>Client</th>
              <th>Promo Disc.</th>
              <th>Discount</th>
              <th>Add. Services</th>
              <th>Actions</th>
            </tr>
            </thead>
            <tbody>
            <tr v-for="item in invoice.items" :key="item.id" class="dd-table__row">
              <td><span class="order-id">#{{ item.id || '—' }}</span></td>
              <td class="text-muted">{{ item.provider_invoice_id || '—' }}</td>
              <td><span class="item-title">{{ item.meta.title || '—' }}</span></td>
              <td>
                <div v-if="Array.isArray(item.meta.additional_services) && item.meta.additional_services.length" class="additional-services">
                  <div v-for="(service, serviceIndex) in item.meta.additional_services" :key="serviceIndex" class="additional-services__line">
                    {{ service.title }} — {{ service.quantity }} × {{ service.amount }} {{ service.currency }}
                  </div>
                  <div v-if="item.meta.additional_services_total != null" class="additional-services__total">
                    Total: {{ item.meta.additional_services_total }} {{ item.selling_price.currency.code }}
                  </div>
                </div>
                <span v-else class="text-muted">—</span>
              </td>
              <td>
                  <span class="link-cell" v-if="item.gift_id" @click="$router.push('/gifts/' + item.gift_id)">
                    #{{ item.gift_id }}
                  </span>
                <span class="text-muted" v-else>—</span>
              </td>
              <td>
                  <span class="link-cell" v-if="item.order_id" @click="$router.push('/orders/' + item.order_id)">
                    #{{ item.order_id }}
                  </span>
                <span class="text-muted" v-else>—</span>
              </td>
              <td>
                  <span class="state-text">
                    {{ item.gift ? item.gift?.status : item.order_id ? 'dated' : 'open' }}
                  </span>
              </td>
              <td v-html="getStatusBadge(item)"></td>
              <td class="price-cell">{{ item.purchase_price.amount }} <span class="currency">{{ item.purchase_price.currency.code }}</span></td>
              <td class="price-cell">{{ item.selling_price.amount }} <span class="currency">{{ item.selling_price.currency.code }}</span></td>
              <td class="price-cell">{{ item.meta.vat }} <span class="currency">{{ item.selling_price.currency.code }}</span></td>
              <td class="price-cell">{{ item.credit_spent.amount }} <span class="currency">{{ item.credit_spent.currency.code }}</span></td>
              <td>
                  <span class="method-badge" v-if="item.payment_method">
                    <i class="fa fa-credit-card"></i> {{ item.payment_method }}
                  </span>
                <span class="text-muted" v-else>—</span>
              </td>
              <td class="text-muted">{{ item.meta.supplier || item.meta.distributor || '—' }}</td>
              <td class="text-muted">{{ item.meta.client || '—' }}</td>
              <td class="text-muted">{{ item.meta.discounts ? item.meta.discounts.promotion_code : '—' }}</td>
              <td>
                <div class="td-action-row">
                  <span class="text-muted">{{ item.meta.discounts ? item.meta.discounts['discount'] : '0' }}</span>
                  <button class="btn-icon btn-icon--blue" @click="editDiscount(item.id)" title="Edit discount">
                    <i class="fa fa-edit"></i>
                  </button>
                </div>
              </td>
              <td>
                <button class="btn-icon btn-icon--blue" @click="showAdditionalServicesCostModal(item)" title="Additional services">
                  <i class="fa fa-plus-circle"></i>
                </button>
              </td>
              <td>
                <button
                    class="btn-icon btn-icon--red"
                    title="Cancel item"
                    :disabled="item.status !== 'purchased'"
                    @click="showCancellationModal(item)"
                >
                  <i class="fa fa-ban"></i>
                </button>
              </td>
            </tr>
            </tbody>
          </table>
        </div>
      </div>

      <!-- ── Summary ── -->
      <div class="summary-panel">
        <div class="panel-title"><i class="fa fa-calculator"></i> Price Summary</div>
        <div class="summary-rows">
          <div class="summary-row">
            <span>Number of Items</span>
            <span class="summary-val">{{ invoice.items.length }}</span>
          </div>
          <div class="summary-row">
            <span>Total Price</span>
            <span class="summary-val">{{ invoice.meta['total_price'] }} AED</span>
          </div>
          <div class="summary-row">
            <span>Delivery Cost</span>
            <span class="summary-val">{{ invoice.meta.delivery_cost | 0 }} AED</span>
          </div>
          <div class="summary-row">
            <span>Additional Cost</span>
            <span class="summary-val">{{ invoice.additional_cost }} AED</span>
          </div>
          <div class="summary-row" v-if="invoice.meta['vat']">
            <span>VAT (5%)</span>
            <span class="summary-val">{{ invoice.meta['vat'] }} AED</span>
          </div>
          <div class="summary-row">
            <span>Discount</span>
            <span class="summary-val text-accent">{{ invoice.discount || '0.00' }} AED</span>
          </div>
          <div class="summary-row">
            <span>Promo Code Discount</span>
            <span class="summary-val text-accent">{{ invoice.promotion_code_discount || '0.00' }} AED</span>
          </div>
          <div class="summary-row" v-if="invoice.cart && invoice.cart.promotion">
            <span>Promo Code Used</span>
            <span class="summary-val">
              <router-link
                  :to="{ name: 'promotions.show', params: { id: invoice.cart.promotion.id } }"
                  class="link-cell"
              >{{ invoice.cart.promotion.code }}</router-link>
            </span>
          </div>
          <div class="summary-row">
            <span>Paid Credit</span>
            <span class="summary-val text-accent">{{ invoice.items_sum_credit_spent }} AED</span>
          </div>
          <div class="summary-row summary-row--total">
            <span>Payable</span>
            <span class="summary-val summary-val--green">{{ invoice.payable.amount }} AED</span>
          </div>
        </div>
      </div>

    </div>

    <!-- Loading -->
    <div class="mt-5 pt-5">
      <loading v-if="!loaded"></loading>
    </div>

    <!-- ── Additional Services Modal ── -->
    <modal name="additional-services-costs-modal" height="auto">
      <div class="dd-modal">
        <div class="dd-modal__header">
          <span class="dd-modal__title">Additional Services Costs</span>
          <button class="dd-modal__close" @click="$modal.hide('additional-services-costs-modal')">
            <i class="fa fa-times"></i>
          </button>
        </div>
        <div class="dd-modal__body">
          <key-value-input
              v-model="itemAdditionalServicesCosts.data"
              :editData="itemAdditionalServicesCosts.data"
          ></key-value-input>
        </div>
        <div class="dd-modal__footer">
          <button class="btn-cancel" @click="$modal.hide('additional-services-costs-modal')">Cancel</button>
          <button class="btn-submit" @click.prevent="updateAdditionalServicesCosts">
            <i class="fa fa-check"></i> Save
          </button>
        </div>
      </div>
    </modal>

    <!-- ── Cancellation Modal ── -->
    <modal name="cancellation-modal" height="auto">
      <div class="dd-modal">
        <div class="dd-modal__header">
          <span class="dd-modal__title">Cancel Item</span>
          <button class="dd-modal__close" @click="$modal.hide('cancellation-modal')">
            <i class="fa fa-times"></i>
          </button>
        </div>
        <div class="dd-modal__body">
          <div class="form-field">
            <label class="filter-label">Refunding Method</label>
            <select v-model="cancellation.refunding_method" class="dd-input">
              <option value="manual">Refund</option>
              <option value="balance">Balance</option>
            </select>
          </div>
          <div class="form-field mt-3">
            <label class="filter-label">Refunding Penalty</label>
            <input v-model="cancellation.refunding_penalty" class="dd-input" type="text" placeholder="0.00" />
          </div>
        </div>
        <div class="dd-modal__footer">
          <button class="btn-cancel" @click="$modal.hide('cancellation-modal')">Cancel</button>
          <button class="btn-submit btn-submit--red" @click.prevent="cancelItem">
            <i class="fa fa-ban"></i> Confirm Cancel
          </button>
        </div>
      </div>
    </modal>

  </div>
</template>

<script>
import { config } from "../../helpers";
import KeyValueInputVue from "../../components/KeyValueInput.vue";

export default {
  components: { "key-value-input": KeyValueInputVue },

  data() {
    return {
      id: this.$route.params.id,
      loaded: false,
      invoice: null,
      itemAdditionalServicesCosts: { id: null, data: {} },
      cancellation: { invoiceItemId: null, refunding_method: null, refunding_penalty: null },
    };
  },

  mounted() {
    this.url = config("BACKEND_ROOT") + "/admin/invoices";
    this.loadData();
  },

  methods: {
    loadData() {
      axios.get(`${this.url}/${this.id}`).then((r) => {
        this.invoice = r.data.data;
        this.loaded  = true;
      });
    },

    editDiscount(itemId) {
      const value = prompt("Enter a number for the discount...");
      if (value === null) return;
      axios.patch(`${this.url}/${this.id}/items/${itemId}`, { field: "meta->discounts->discount", value }).then(() => this.loadData());
    },

    async editDiscounts() {
      const value = prompt("Enter a number for the discount...");
      if (value === null) return;
      for (const item of this.invoice.items) {
        await axios.patch(`${this.url}/${this.id}/items/${item.id}`, { field: "meta->discounts->discount", value });
      }
      this.loadData();
    },

    showAdditionalServicesCostModal(invoiceItem) {
      this.itemAdditionalServicesCosts.id   = invoiceItem.id;
      this.itemAdditionalServicesCosts.data = invoiceItem.meta.additional_services_costs ?? { service: 0 };
      if (!this.itemAdditionalServicesCosts.data || Object.keys(this.itemAdditionalServicesCosts.data).length === 0) {
        this.itemAdditionalServicesCosts.data = { service: 0 };
      }
      this.$modal.show("additional-services-costs-modal");
    },

    updateAdditionalServicesCosts() {
      axios
          .patch(`${this.url}/${this.id}/items/${this.itemAdditionalServicesCosts.id}`, {
            field: "meta->additional_services_costs",
            value: this.itemAdditionalServicesCosts.data ?? {},
          })
          .then(() => { this.$modal.hide("additional-services-costs-modal"); this.loadData(); })
          .catch(() => alert("Something went wrong!"));
    },

    showCancellationModal(invoiceItem) {
      this.cancellation.invoiceItemId = invoiceItem.id;
      this.$modal.show("cancellation-modal");
    },

    cancelItem() {
      axios
          .post(`${this.url}/${this.id}/items/${this.cancellation.invoiceItemId}/cancel`, this.cancellation)
          .then(() => { alert("Item cancelled successfully!"); this.loadData(); this.$modal.hide("cancellation-modal"); })
          .catch((e) => {
            const msg = e.response.data?.errors
                ? Object.values(e.response.data.errors).flat()[0]
                : e.response.data?.meta?.message;
            alert(msg);
          });
    },

    formatDate(date) {
      if (!date) return null;
      const d = new Date(date);
      return `${d.getFullYear()}-${d.getMonth()+1}-${d.getDate()} ${d.getHours()}:${String(d.getMinutes()).padStart(2,"0")}`;
    },

    getStatusBadge(item) {
      const map = {
        refunded:  ["status-badge status-badge--red",   "Refunded"],
        canceled:  ["status-badge status-badge--red",   "Canceled"],
        purchased: ["status-badge status-badge--green", "Purchased"],
      };
      const [cls, label] = map[item.status] ?? ["status-badge status-badge--gray", item.status];
      return `<span class="${cls}">${label}</span>`;
    },
  },
};
</script>

<style scoped>
/* ══════════════════════════════════════
   Page
══════════════════════════════════════ */
.page-wrap { display: flex; flex-direction: column; gap: 16px; }

/* ── Header ── */
.page-header { display: flex; align-items: flex-start; justify-content: space-between; gap: 16px; flex-wrap: wrap; }
.page-header__left { display: flex; align-items: flex-start; gap: 12px; }
.btn-back { width: 34px; height: 34px; margin-top: 4px; background: var(--bg-hover); border: 1px solid var(--border); border-radius: 8px; color: var(--text-2); cursor: pointer; display: flex; align-items: center; justify-content: center; flex-shrink: 0; transition: border-color .15s, color .15s; }
.btn-back:hover { border-color: var(--accent); color: var(--accent); }
.page-title   { font-size: 20px; font-weight: 500; color: var(--text-1); margin: 0 0 6px; font-family: monospace; }
.page-meta    { display: flex; flex-wrap: wrap; gap: 12px; align-items: center; }
.meta-item    { font-size: 12px; color: var(--text-3); display: flex; align-items: center; gap: 5px; }

/* ── Info grid ── */
.info-grid { display: grid; grid-template-columns: repeat(4, 1fr); gap: 12px; }
.info-card { background: var(--bg-panel); border: 1px solid var(--border); border-radius: 12px; padding: 1rem 1.1rem; transition: border-color .15s; }
.info-card--link { cursor: pointer; }
.info-card--link:hover { border-color: var(--accent); }
.info-card__label { font-size: 11px; font-weight: 500; color: var(--text-3); text-transform: uppercase; letter-spacing: .5px; margin-bottom: 6px; }
.info-card__value { font-size: 14px; font-weight: 500; color: var(--text-1); }
.mono { font-family: monospace; }
.payable-val { font-size: 20px; font-weight: 600; color: #22c55e; }
.currency { font-size: 12px; font-weight: 400; color: var(--text-3); margin-left: 3px; }
.link-cell { color: var(--accent); cursor: pointer; font-weight: 500; }
.link-cell:hover { text-decoration: underline; }
.text-muted { color: var(--text-3); font-size: 12px; }

/* ── Table panel ── */
.table-panel { background: var(--bg-panel); border: 1px solid var(--border); border-radius: 12px; overflow: hidden; }
.table-panel__header { display: flex; align-items: center; justify-content: space-between; padding: .9rem 1.25rem; border-bottom: 1px solid var(--border); }
.panel-title { font-size: 12px; font-weight: 600; color: var(--text-3); text-transform: uppercase; letter-spacing: .5px; display: flex; align-items: center; gap: 7px; }
.btn-edit-all { height: 30px; padding: 0 12px; background: var(--bg-hover); border: 1px solid var(--border); border-radius: 7px; color: var(--text-2); font-size: 12px; cursor: pointer; display: flex; align-items: center; gap: 6px; transition: border-color .15s, color .15s; }
.btn-edit-all:hover { border-color: var(--accent); color: var(--accent); }

.table-responsive-wrap { overflow-x: auto; }
.dd-table { width: 100%; border-collapse: collapse; font-size: 12px; }
.dd-table thead tr { border-bottom: 1px solid var(--border); }
.dd-table th { padding: 9px 12px; font-size: 10px; font-weight: 600; text-transform: uppercase; letter-spacing: .5px; color: var(--text-3); white-space: nowrap; text-align: left; }
.dd-table td { padding: 11px 12px; color: var(--text-2); border-bottom: 1px solid var(--border); vertical-align: middle; white-space: nowrap; }
.dd-table__row:hover td { background: var(--bg-hover); }
.dd-table tr:last-child td { border-bottom: none; }

.order-id   { font-weight: 500; color: var(--text-1); }
.item-title { font-weight: 500; color: var(--text-1); max-width: 180px; overflow: hidden; text-overflow: ellipsis; display: block; }
.price-cell { font-weight: 500; color: var(--text-1); }
.additional-services { min-width: 180px; white-space: normal; overflow-wrap: anywhere; }
.additional-services__line + .additional-services__line { margin-top: 3px; }
.additional-services__total { margin-top: 4px; font-weight: 500; color: var(--text-1); }
.state-text { font-size: 11px; text-transform: capitalize; color: var(--text-2); }

.method-badge { display: inline-flex; align-items: center; gap: 5px; padding: 2px 8px; border-radius: 20px; font-size: 11px; background: rgba(129,140,248,.12); color: #818cf8; border: 1px solid rgba(129,140,248,.2); }

.td-action-row { display: flex; align-items: center; gap: 8px; }

.btn-icon { width: 28px; height: 28px; border-radius: 6px; border: 1px solid var(--border); background: var(--bg-hover); display: flex; align-items: center; justify-content: center; cursor: pointer; font-size: 12px; color: var(--text-2); transition: border-color .15s, color .15s, background .15s; }
.btn-icon:disabled { opacity: .35; cursor: not-allowed; }
.btn-icon--blue:hover { border-color: #818cf8; color: #818cf8; background: rgba(129,140,248,.08); }
.btn-icon--red:hover  { border-color: var(--accent); color: var(--accent); background: rgba(244,47,75,.08); }

/* ── Summary ── */
.summary-panel { background: var(--bg-panel); border: 1px solid var(--border); border-radius: 12px; padding: 1.25rem; max-width: 380px; margin-left: auto; }
.summary-rows { display: flex; flex-direction: column; }
.summary-row { display: flex; justify-content: space-between; align-items: center; padding: 9px 0; border-bottom: 1px solid var(--border); font-size: 13px; color: var(--text-2); }
.summary-row:last-child { border-bottom: none; }
.summary-row--total { padding-top: 12px; margin-top: 4px; }
.summary-val { font-weight: 500; color: var(--text-1); }
.summary-val--green { color: #22c55e; font-size: 16px; }
.text-accent { color: var(--accent); }

/* ── Modal ── */
.dd-modal { background: var(--bg-panel); border-radius: 12px; overflow: hidden; }
.dd-modal__header { display: flex; align-items: center; justify-content: space-between; padding: 1rem 1.25rem; border-bottom: 1px solid var(--border); }
.dd-modal__title  { font-size: 15px; font-weight: 500; color: var(--text-1); }
.dd-modal__close  { width: 28px; height: 28px; background: var(--bg-hover); border: 1px solid var(--border); border-radius: 6px; color: var(--text-2); cursor: pointer; display: flex; align-items: center; justify-content: center; transition: border-color .15s; }
.dd-modal__close:hover { border-color: var(--accent); color: var(--accent); }
.dd-modal__body   { padding: 1.25rem; }
.dd-modal__footer { display: flex; justify-content: flex-end; gap: 8px; padding: .75rem 1.25rem; border-top: 1px solid var(--border); }
.form-field { display: flex; flex-direction: column; gap: 5px; }
.filter-label { font-size: 11px; font-weight: 500; color: var(--text-3); text-transform: uppercase; letter-spacing: .4px; }
.dd-input { height: 36px; background: var(--bg-base); border: 1px solid var(--border); border-radius: 8px; color: var(--text-1); font-size: 13px; padding: 0 10px; outline: none; width: 100%; transition: border-color .15s; }
.dd-input:focus { border-color: var(--accent); }
.btn-cancel { height: 36px; padding: 0 16px; background: var(--bg-hover); border: 1px solid var(--border); border-radius: 8px; color: var(--text-2); font-size: 13px; cursor: pointer; }
.btn-submit { height: 36px; padding: 0 16px; background: var(--accent); border: none; border-radius: 8px; color: #fff; font-size: 13px; font-weight: 500; cursor: pointer; display: flex; align-items: center; gap: 6px; transition: opacity .15s; }
.btn-submit:hover { opacity: .88; }
.btn-submit--red { background: #f42f4b; }

/* ══════════════════════════════════════
   Responsive
══════════════════════════════════════ */
@media (max-width: 1024px) { .info-grid { grid-template-columns: repeat(2, 1fr); } }
@media (max-width: 600px)  { .info-grid { grid-template-columns: 1fr 1fr; } .summary-panel { max-width: 100%; } }
</style>
